import React, { useCallback, useEffect, useReducer, useState } from "react";
import { Button, Card, Col, Form, Row, Spinner, Table } from "@themesberg/react-bootstrap";
import moment from "moment";
import { useCookies } from "react-cookie";
import { useLocation } from "react-router-dom";
import Pagination from "../../components/Pagination";
import { getStatistic, getStatisticSummary } from "../../controller/api/lotoThudoStatisticByTicket";
import { calculatePage, parseQuery } from "../../helper/helper";
import { getLotoTypeLabelWithFilter } from "../../helper/lotoType";

const initialData = {
  filter: {
    phone: "",
    page: 0,
  },
  data: {
    list: [],
    total: 0,
    totalPage: 0,
  },
  summary: {
    total: 0,
    totalReward: 0,
    totalUnReward: 0,
  },
  isLoaded: false,
  isLoading: false,
  filterPressCount: 0,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "SUBMIT_FILTER":
      return {
        ...state,
        filter: {
          ...action.payload,
          page: 0,
        },
        filterPressCount: state.filterPressCount + 1,
      };
    case "UPDATE_DATA":
      return {
        ...state,
        data: {
          ...action.payload,
        },
        isLoaded: true,
      };
    case "UPDATE_SUMMARY":
      return {
        ...state,
        summary: {
          ...action.payload,
        },
      };
    case "UPDATE_PAGE":
      return {
        ...state,
        filter: {
          ...state.filter,
          page: action.payload,
        },
      };
    case "SET_LOADING":
      return {
        ...state,
        isLoading: Boolean(action.payload),
      };
    default:
      return state;
  }
};

export default function LotoThudoStatistic() {
  const [state, dispatch] = useReducer(reducer, initialData);
  const [cookies] = useCookies(["lottmekong"]);
  const location = useLocation();
  const [phoneInput, setPhoneInput] = useState("");

  const loadData = useCallback(async () => {
    const queryObj = parseQuery(location.search);

    if (!queryObj.gamePeriodId) {
      dispatch({
        type: "UPDATE_DATA",
        payload: {
          list: [],
          total: 0,
          totalPage: 0,
        },
      });
      dispatch({
        type: "UPDATE_SUMMARY",
        payload: {
          total: 0,
          totalReward: 0,
          totalUnReward: 0,
        },
      });
      return;
    }

    dispatch({ type: "SET_LOADING", payload: true });

    try {
      let phone = state.filter.phone;
      if (phone.indexOf("MB") < 0) {
        phone = phone.replace("0", "+84");
      }

      const [statisticResponse, summaryResponse] = await Promise.all([
        getStatistic(
          cookies.lottmekong,
          {
            phone,
            gamePeriodId: queryObj.gamePeriodId,
          },
          state.filter.page,
        ),
        getStatisticSummary(cookies.lottmekong, {
          phone,
          gamePeriodId: queryObj.gamePeriodId,
        }),
      ]);

      if (statisticResponse) {
        dispatch({
          type: "UPDATE_DATA",
          payload: {
            list: statisticResponse.data,
            total: statisticResponse.total,
            totalPage: calculatePage(statisticResponse.total),
          },
        });
      }

      if (summaryResponse) {
        dispatch({
          type: "UPDATE_SUMMARY",
          payload: {
            total: summaryResponse.total,
            totalReward: summaryResponse.totalReward || 0,
            totalUnReward: summaryResponse.totalUnReward || 0,
          },
        });
      }
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [cookies.lottmekong, location.search, state.filter.page, state.filter.phone]);

  useEffect(() => {
    document.title = "Thống kê điện toán Thủ Đô";
    loadData();
  }, [loadData, state.filterPressCount]);

  return (
    <div className="py-4">
      <Card border="light" className="shadow-sm mb-4">
        <Card.Header>
          <Row className="align-items-center">
            <Col className="align-self-center">
              <h5>THỐNG KÊ ĐIỆN TOÁN THỦ ĐÔ</h5>
            </Col>
            <Col className="text-right">
              <Button
                variant="info"
                type="button"
                onClick={() =>
                  dispatch({
                    type: "SUBMIT_FILTER",
                    payload: {
                      phone: phoneInput,
                    },
                  })
                }
                disabled={state.isLoading}
              >
                {state.isLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Đang tải
                  </>
                ) : (
                  "Tìm kiếm"
                )}
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <Row className="p-1">
            <Col md={6}>
              <Row>
                <Col md={3} className="text-right align-self-center">
                  <Form.Label>Số điện thoại</Form.Label>
                </Col>
                <Col md={9}>
                  <Form.Control
                    value={phoneInput}
                    onChange={(event) => setPhoneInput(event.target.value)}
                    type="text"
                    placeholder="Nhập số điện thoại"
                    disabled={state.isLoading}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card border="light" className="shadow-sm mb-4">
        <Card.Body>
          <Row>
            <Col md={4} className="text-center">
              <Form.Label>Tổng số vé</Form.Label>
              <div>{new Intl.NumberFormat("vn-VI").format(state.summary.total)}</div>
            </Col>
            <Col md={4} className="text-center">
              <Form.Label>Trúng thưởng</Form.Label>
              <div>{new Intl.NumberFormat("vn-VI").format(state.summary.totalReward)}</div>
            </Col>
            <Col md={4} className="text-center">
              <Form.Label>Không trúng</Form.Label>
              <div>{new Intl.NumberFormat("vn-VI").format(state.summary.totalUnReward)}</div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="position-relative">
        {!state.isLoaded ? (
          <Card border="light" className="shadow-sm">
            <Card.Body className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </Card.Body>
          </Card>
        ) : (
          <>
            <Card border="light" className="shadow-sm">
              <Table responsive className="align-items-center table-bordered table-hover mb-0">
                <thead className="text-center">
                  <tr>
                    <th scope="col">Người chơi</th>
                    <th scope="col">Mã đơn</th>
                    <th scope="col">Cách chơi</th>
                    <th scope="col">Dãy số</th>
                    <th scope="col">Giá trị</th>
                    <th scope="col">Trạng thái</th>
                    <th scope="col">Ngày xuất</th>
                  </tr>
                </thead>
                <tbody>
                  {state.data.list && state.data.list.length > 0 ? (
                    state.data.list.map((item) => (
                      <tr key={item.id}>
                        <td>
                          {item.userName || ""}
                          <br />
                          <strong className="text-primary">{item.userPhone || ""}</strong>
                        </td>
                        <td className="text-center">{item.cartId}</td>
                        <td className="text-center">
                          <strong>{getLotoTypeLabelWithFilter(item.lotoType, item.baoFilter, item.baoDigits)}</strong>
                        </td>
                        <td className="text-center">
                          <strong>{(item.numbers || "").split("").join(" ")}</strong>
                        </td>
                        <td className="text-center">
                          {new Intl.NumberFormat("vn-VI").format(item.value)}
                        </td>
                        <td
                          className={`text-center${
                            item.status === 4 ? " text-danger" : item.status === 3 ? " text-warning" : ""
                          }`}
                        >
                          {item.status === 4
                            ? "Trúng thưởng"
                            : item.status === 5
                              ? "Không trúng"
                              : "Chờ tính lại"}
                        </td>
                        <td className="text-center">
                          {moment(item.confirmAt || "").format("DD/MM/YYYY HH:mm").toLocaleString()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center py-5 text-muted">
                        Không có dữ liệu
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
              <Card.Footer className="flex-row">
                <Row className="align-items-center">
                  <Col>
                    <Pagination
                      totalPage={state.data.totalPage || 0}
                      currentPage={state.filter.page || 0}
                      onPageChange={(page) =>
                        dispatch({
                          type: "UPDATE_PAGE",
                          payload: page,
                        })
                      }
                    />
                  </Col>
                </Row>
              </Card.Footer>
            </Card>

            {state.isLoading && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                style={{
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(1px)",
                }}
              >
                <Spinner animation="border" variant="primary" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
